import { useState, useEffect } from "react";
import { format } from "date-fns";
import { Play, AlertTriangle, PlusCircle, Send, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";

interface ReopenSessionGuardProps {
  session: {
    id: string;
    topic?: string | null;
    module_code?: string | null;
    closed_at?: string | null;
  };
  onReopen: () => void;
  onStartNew: () => void;
}

/**
 * Posted/closed sessions can no longer be re-opened with a typed confirmation.
 * Superadmin keeps the direct reopen power; everyone else must submit a
 * Re-scan Request that Principal/Superadmin approve.
 */
export function ReopenSessionGuard({ session, onReopen, onStartNew }: ReopenSessionGuardProps) {
  const { user, hasRole } = useAuth();
  const { toast } = useToast();
  const isSuperadmin = hasRole("superadmin");

  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [pendingReq, setPendingReq] = useState<{ id: string; status: string; created_at: string } | null>(null);

  const requiredPhrase = (session.module_code || "REOPEN").trim();
  const matches = confirmText.trim().toUpperCase() === requiredPhrase.toUpperCase();

  // Look up if there is already a pending request for this session
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      const { data } = await (supabase as any)
        .from("session_reopen_requests")
        .select("id, status, created_at")
        .eq("session_id", session.id)
        .eq("status", "pending")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (!cancelled) setPendingReq(data ?? null);
    })();
    return () => { cancelled = true; };
  }, [open, session.id]);

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (!next) { setConfirmText(""); setReason(""); }
  }

  async function submitRequest() {
    if (reason.trim().length < 5) {
      toast({ title: "Reason required", description: "Please describe why this session needs to be re-scanned (5+ chars).", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    const { error } = await (supabase as any).from("session_reopen_requests").insert({
      session_id: session.id,
      requested_by: user?.id,
      reason: reason.trim(),
    });
    setSubmitting(false);
    if (error) {
      toast({ title: "Could not submit", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Re-scan request sent", description: "Awaiting Principal/Superadmin approval." });
    handleOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="default" size="sm" title={isSuperadmin ? "Reopen this session" : "Request re-scan approval"}>
          {isSuperadmin ? <Play className="w-4 h-4 mr-1" /> : <Send className="w-4 h-4 mr-1" />}
          {isSuperadmin ? "Reopen" : "Request Re-scan"}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-destructive" />
            This session is already posted
          </DialogTitle>
          <DialogDescription className="space-y-2">
            <span className="block">
              "{session.topic || "Untitled"}"
              {session.module_code ? ` (${session.module_code})` : ""} was closed
              {session.closed_at ? ` on ${format(new Date(session.closed_at), "PPp")}` : ""}.
            </span>
            <span className="block font-medium text-foreground">
              {isSuperadmin
                ? "We strongly recommend starting a new session instead — reopening overwrites the existing summary and is recorded in the audit log."
                : "Re-scanning a posted session requires Principal/Superadmin approval. Submit a request with a brief reason below."}
            </span>
          </DialogDescription>
        </DialogHeader>

        {pendingReq && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            <span>A re-scan request is already <Badge variant="outline">pending</Badge> for this session (submitted {format(new Date(pendingReq.created_at), "PPp")}).</span>
          </div>
        )}

        {isSuperadmin ? (
          <div className="rounded-md border border-border bg-muted/40 p-3 space-y-2">
            <Label htmlFor="reopen-confirm" className="text-sm">
              To reopen anyway, type{" "}
              <span className="font-mono font-semibold text-foreground">{requiredPhrase}</span> below
            </Label>
            <Input
              id="reopen-confirm"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={requiredPhrase}
              autoComplete="off"
            />
          </div>
        ) : (
          <div className="space-y-2">
            <Label htmlFor="reopen-reason" className="text-sm">Reason for re-scan *</Label>
            <Textarea
              id="reopen-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Wrong attendance posted; need to correct before reporting."
              rows={3}
              disabled={!!pendingReq}
            />
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => handleOpenChange(false)}>Cancel</Button>
          {isSuperadmin ? (
            <>
              <Button
                variant="destructive"
                disabled={!matches}
                onClick={() => { handleOpenChange(false); onReopen(); }}
              >
                Reopen anyway
              </Button>
              <Button variant="default" onClick={() => { handleOpenChange(false); onStartNew(); }}>
                <PlusCircle className="w-4 h-4 mr-1" />Start a new session
              </Button>
            </>
          ) : (
            <Button onClick={submitRequest} disabled={submitting || !!pendingReq}>
              <Send className="w-4 h-4 mr-1" />Submit request
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
